/**
 * Coarse byte-size estimate for an in-memory dataset, intended for diagnostic
 * logging. Uses `JSON.stringify(value).length` as a proxy:
 *   - ASCII chars ≈ 1 byte (UTF-16 in memory = 2 bytes, but JSON.stringify
 *     output is the wire representation, which is what matters for "how much
 *     did we just load from Firebase").
 *   - Class instances (Payment, GenericRecord) get serialized via their own
 *     enumerable fields; private underscored fields (`_date`, `_zahlungsBetrag`)
 *     ARE captured, getters are NOT - close enough for back-of-envelope.
 * For Maps, we serialize `Array.from(map.values())`.
 *
 * NOT for hot paths - JSON.stringify on multi-MB datasets is expensive. Call
 * inside a useEffect keyed to the dataset's actual identity change.
 */
const stringifySafe = (value: unknown): number => {
    try {
        return JSON.stringify(value)?.length ?? 0;
    } catch {
        // Cyclic references, BigInt, etc. - return 0 so logging doesn't crash.
        return 0;
    }
};

const formatMB = (bytes: number): string => `${(bytes / 1024 / 1024).toFixed(2)} MB`;

type LoggableDataset =
    | { type: "map"; map: Map<unknown, unknown> }
    | { type: "list"; list: unknown[] }
    | { type: "object"; value: unknown };

/**
 * Logs a single line per dataset to console:
 *   [DATASET] <label> - N entries, ~X MB (avg ~Y KB/entry)
 *
 * Wire from a useEffect on the dataset identity:
 *   useEffect(() => logDatasetSize("Payments", { type: "list", list: payments }), [payments]);
 */
export const logDatasetSize = (label: string, dataset: LoggableDataset): void => {
    if (!import.meta.env.DEV) {
        return;
    }
    let entries = 0;
    let bytes = 0;
    switch (dataset.type) {
        case "map":
            entries = dataset.map.size;
            bytes = stringifySafe(Array.from(dataset.map.values()));
            break;
        case "list":
            entries = dataset.list.length;
            bytes = stringifySafe(dataset.list);
            break;
        case "object":
            entries = 1;
            bytes = stringifySafe(dataset.value);
            break;
    }
    const avgKB = entries > 0 ? (bytes / entries / 1024).toFixed(2) : "0.00";
};
